/** @format */

import {
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  Hash,
  Loader,
  User,
} from "lucide-react";
import { FC, useState } from "react";
import { MigrateSopDialog } from "../../components/sop/MigrateSopDialog";
import { useOrganizationStore } from "../../store/useOrganizationStore";
import type { SopListFilters } from "../../store/useSopStore";
import { useTrackChangesState } from "../../store/useTrackChangesStore";
import { SOP } from "../../types/sop";
import SOPItem from "./SOPItem";

const DEFAULT_PAGE_SIZE = 20;

const GRID_WITH_ORG = "minmax(250px, 1fr) 180px 160px 100px 170px 90px";
const GRID_NO_ORG = "minmax(250px, 1fr) 180px 100px 170px 90px";

interface SopListProps {
  canEdit?: boolean;
  canAccessRMA?: boolean;
  isSuperAdmin?: boolean;
  appliedFilters?: SopListFilters;
  pageSize?: number;
}

const SopList: FC<SopListProps> = ({
  canEdit = false,
  canAccessRMA = false,
  isSuperAdmin = false,
  appliedFilters = {},
  pageSize = DEFAULT_PAGE_SIZE,
}) => {
  const { sops, isLoading, pagination, fetchSOPs } = useTrackChangesState();
  const { organizations, activeOrgId } = useOrganizationStore();
  const [migrateSop, setMigrateSop] = useState<SOP | null>(null);

  const page = pagination?.page ?? 1;
  const total = pagination?.total ?? 0;
  const limit = pagination?.limit ?? pageSize;
  const hasNext = pagination?.hasNext ?? false;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handlePrevPage = () => {
    if (page <= 1) return;
    fetchSOPs({ page: page - 1, limit, ...appliedFilters });
  };

  const handleNextPage = () => {
    if (!hasNext) return;
    fetchSOPs({ page: page + 1, limit, ...appliedFilters });
  };

  const gridTemplateColumns = isSuperAdmin ? GRID_WITH_ORG : GRID_NO_ORG;

  const renderList = () =>
    sops.map((sop) => (
      <SOPItem
        key={sop.sopId}
        sop={sop}
        canEdit={canEdit}
        canAccessRMA={canAccessRMA}
        isSuperAdmin={isSuperAdmin}
        gridTemplateColumns={gridTemplateColumns}
        displayOrgName={
          isSuperAdmin
            ? (sop.organizationName ??
              (sop.organizationId
                ? organizations.find((o) => o.orgId === sop.organizationId)
                  ?.name
                : "—"))
            : undefined
        }
        onMigrateClick={isSuperAdmin ? () => setMigrateSop(sop) : undefined}
      />
    ));

  return isLoading ? (
    <div className="flex justify-center items-center h-64">
      <div className="flex flex-col items-center gap-4">
        <Loader className="animate-spin w-8 h-8 text-blue-400" />
        <p className="text-slate-400">Loading SOPs...</p>
      </div>
    </div>
  ) : (
    <div className="w-full">
      {/* Table Header */}
      <div
        className="grid gap-4 p-4 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/30 rounded-t-xl"
        style={{ gridTemplateColumns }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="font-semibold text-sm text-white truncate">Title</span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <User className="w-4 h-4 text-amber-400" />
          <span className="font-semibold text-sm text-white truncate">Creator</span>
        </div>
        {isSuperAdmin && (
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-4 h-4 text-purple-400" />
            <span className="font-semibold text-sm text-white truncate">Organization</span>
          </div>
        )}
        <div className="flex items-center justify-center gap-2">
          <Hash className="w-4 h-4 text-cyan-400" />
          <span className="font-semibold text-sm text-white">Ops</span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Calendar className="w-4 h-4 text-green-400 shrink-0" />
          <span className="font-semibold text-sm text-white truncate">Created At</span>
        </div>
        <div className="flex items-center justify-center">
          <span className="font-semibold text-sm text-white">Actions</span>
        </div>
      </div>

      {/* Table Body */}
      <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 border-l border-r border-b border-slate-600/30 rounded-b-xl overflow-hidden">
        {sops.length > 0 ? (
          <div className="divide-y divide-slate-700/30">{renderList()}</div>
        ) : (
          <div className="text-center py-16">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-700/50 to-slate-800/50 rounded-full flex items-center justify-center border border-slate-600/30">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  No SOPs Found
                </h3>
                <p className="text-slate-400 max-w-md">
                  Create your first Standard Operating Procedure to get started
                  with AI-powered manufacturing documentation.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-slate-600/30 bg-slate-800/20">
          <p className="text-slate-400 text-sm">
            Page {page} of {totalPages}
            {total > 0 && <span className="ml-2">({total} total)</span>}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={!hasNext}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {migrateSop && (
        <MigrateSopDialog
          open={!!migrateSop}
          onClose={() => setMigrateSop(null)}
          sopId={migrateSop.sopId}
          sopTitle={migrateSop.getDisplayTitle()}
          currentOrgId={
            migrateSop.organizationId ?? activeOrgId ?? undefined
          }
          organizations={organizations}
          onSuccess={() =>
            fetchSOPs({ page: 1, limit, ...appliedFilters })
          }
        />
      )}
    </div>
  );
};

export default SopList;
